import React from 'react';
import { Callout, DefaultButton, DirectionalHint, IComboBoxOption, ITextFieldStyles, TextField } from '@fluentui/react';

type SavedShipToAddressPickerProps = {
    id: string;
    label?: string;
    ariaLabel?: string;
    placeholder?: string;
    styles?: any;
    options: IComboBoxOption[];
    selectedKey?: string | number;
    selectedText?: string;
    searchText?: string;
    disabled?: boolean;
    onInputValueChange?: (value: string) => void;
    onOptionSelect?: (option?: IComboBoxOption) => void | Promise<void>;
};

export function SavedShipToAddressPicker(props: SavedShipToAddressPickerProps) {
    const {
        id,
        label,
        ariaLabel,
        placeholder,
        styles,
        options,
        selectedKey,
        selectedText,
        searchText,
        disabled,
        onInputValueChange,
        onOptionSelect,
    } = props;

    const [isOpen, setIsOpen] = React.useState(false);
    const [activeIndex, setActiveIndex] = React.useState<number>(-1);
    const containerId = `${id}-container`;
    const listboxId = `${id}-listbox`;
    const getOptionId = (key: string | number) => `${id}-option-${String(key)}`;
    const dropdownWidth = styles?.dropdown?.width || 300;
    const displayValue = searchText || selectedText || '';
    const textFieldStyles: Partial<ITextFieldStyles> = {
        root: { width: dropdownWidth },
        fieldGroup: {
            minHeight: 32,
            borderRadius: 0,
            paddingRight: 32,
        },
        field: {
            fontSize: styles?.title?.fontSize || '11px',
            height: styles?.title?.height || '32px',
            padding: '0 8px',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
        },
        errorMessage: styles?.errorMessage,
    };

    const onChange = (_event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setIsOpen(true);
        setActiveIndex(previousIndex => (options.length > 0 ? Math.max(previousIndex, 0) : -1));
        onInputValueChange?.(newValue || '');
    };

    const onSelect = async (option?: IComboBoxOption) => {
        setIsOpen(false);
        setActiveIndex(-1);
        await onOptionSelect?.(option);
    };

    const activeDescendantId =
        isOpen && activeIndex >= 0 && activeIndex < options.length ? getOptionId(options[activeIndex].key) : undefined;

    const scrollActiveIntoView = (nextIndex: number) => {
        const key = options[nextIndex]?.key;
        if (key === undefined) {
            return;
        }

        requestAnimationFrame(() => document.getElementById(getOptionId(key))?.scrollIntoView({ block: 'nearest' }));
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (disabled) {
            return;
        }

        switch (event.key) {
            case 'ArrowDown': {
                event.preventDefault();
                if (!isOpen) {
                    const selectedIndex = options.findIndex(option => option.key === selectedKey);
                    const nextIndex = options.length > 0 ? Math.max(selectedIndex, 0) : -1;
                    setIsOpen(true);
                    setActiveIndex(nextIndex);
                    if (nextIndex >= 0) {
                        scrollActiveIntoView(nextIndex);
                    }
                    return;
                }

                setActiveIndex(previousIndex => {
                    const nextIndex = Math.min(Math.max(previousIndex, 0) + 1, options.length - 1);
                    scrollActiveIntoView(nextIndex);
                    return nextIndex;
                });
                return;
            }
            case 'ArrowUp': {
                event.preventDefault();
                if (!isOpen) {
                    const selectedIndex = options.findIndex(option => option.key === selectedKey);
                    const nextIndex = options.length > 0 ? (selectedIndex >= 0 ? selectedIndex : options.length - 1) : -1;
                    setIsOpen(true);
                    setActiveIndex(nextIndex);
                    if (nextIndex >= 0) {
                        scrollActiveIntoView(nextIndex);
                    }
                    return;
                }

                setActiveIndex(previousIndex => {
                    const currentIndex = previousIndex < 0 ? options.length - 1 : previousIndex;
                    const nextIndex = Math.max(currentIndex - 1, 0);
                    scrollActiveIntoView(nextIndex);
                    return nextIndex;
                });
                return;
            }
            case 'Home': {
                if (!isOpen || options.length === 0) {
                    return;
                }

                event.preventDefault();
                setActiveIndex(0);
                scrollActiveIntoView(0);
                return;
            }
            case 'End': {
                if (!isOpen || options.length === 0) {
                    return;
                }

                event.preventDefault();
                const nextIndex = options.length - 1;
                setActiveIndex(nextIndex);
                scrollActiveIntoView(nextIndex);
                return;
            }
            case 'Enter': {
                if (!isOpen || activeIndex < 0 || activeIndex >= options.length) {
                    return;
                }

                event.preventDefault();
                void onSelect(options[activeIndex]);
                return;
            }
            case 'Escape': {
                if (!isOpen) {
                    return;
                }

                event.preventDefault();
                setIsOpen(false);
                setActiveIndex(-1);
                return;
            }
        }
    };

    return (
        <div
            id={containerId}
            role="combobox"
            aria-label={ariaLabel || label}
            aria-autocomplete="list"
            aria-expanded={isOpen && !disabled}
            aria-controls={listboxId}
            aria-activedescendant={activeDescendantId}
            aria-haspopup="listbox"
            style={{ position: 'relative', width: dropdownWidth }}
        >
            <TextField
                id={id}
                label={label}
                ariaLabel={ariaLabel}
                placeholder={placeholder}
                value={displayValue}
                onChange={onChange}
                onFocus={() => {
                    setIsOpen(true);
                    if (options.length > 0) {
                        const selectedIndex = options.findIndex(option => option.key === selectedKey);
                        setActiveIndex(Math.max(selectedIndex, 0));
                    }
                }}
                onKeyDown={onKeyDown}
                disabled={disabled}
                autoComplete="off"
                styles={textFieldStyles}
            />
            <DefaultButton
                ariaLabel={ariaLabel ? `${ariaLabel} toggle` : 'toggle saved addresses'}
                iconProps={{ iconName: 'ChevronDown' }}
                disabled={disabled}
                onClick={() => {
                    if (disabled) {
                        return;
                    }

                    setIsOpen(current => {
                        const next = !current;
                        if (next && options.length > 0) {
                            const selectedIndex = options.findIndex(option => option.key === selectedKey);
                            const nextIndex = Math.max(selectedIndex, 0);
                            setActiveIndex(nextIndex);
                            scrollActiveIntoView(nextIndex);
                        } else if (!next) {
                            setActiveIndex(-1);
                        }

                        return next;
                    });
                }}
                styles={{
                    root: {
                        position: 'absolute',
                        right: 1,
                        bottom: 1,
                        minWidth: 30,
                        width: 30,
                        height: 30,
                        border: 'none',
                        borderRadius: 0,
                        padding: 0,
                        zIndex: 1,
                    },
                    rootHovered: {
                        border: 'none',
                    },
                    icon: {
                        color: '#605e5c',
                        fontSize: 12,
                    },
                }}
            />
            {isOpen && !disabled && (
                <Callout
                    target={`#${containerId}`}
                    onDismiss={() => {
                        setIsOpen(false);
                        setActiveIndex(-1);
                    }}
                    isBeakVisible={false}
                    gapSpace={2}
                    directionalHint={DirectionalHint.bottomLeftEdge}
                    setInitialFocus={false}
                    styles={{
                        root: { width: dropdownWidth },
                        calloutMain: {
                            width: dropdownWidth,
                            maxHeight: 240,
                            overflowY: 'auto',
                        },
                    }}
                >
                    <div id={listboxId} role="listbox" aria-label={ariaLabel || label}>
                        {options.length > 0 ? (
                            options.map((option, index) => {
                                const isSelected = option.key === selectedKey;
                                const isActive = index === activeIndex;

                                return (
                                    <button
                                        key={option.key}
                                        id={getOptionId(option.key)}
                                        role="option"
                                        aria-selected={isSelected}
                                        type="button"
                                        onMouseDown={event => event.preventDefault()}
                                        onMouseEnter={() => setActiveIndex(index)}
                                        onClick={() => onSelect(option)}
                                        style={{
                                            width: '100%',
                                            border: 'none',
                                            background: isActive ? '#c7e0f4' : isSelected ? '#edebe9' : '#ffffff',
                                            cursor: 'pointer',
                                            minHeight: 36,
                                            padding: '8px',
                                            textAlign: 'left',
                                            fontSize: '11px',
                                        }}
                                    >
                                        {option.text}
                                    </button>
                                );
                            })
                        ) : (
                            <div style={{ padding: '8px', fontSize: '11px' }}>No saved addresses found.</div>
                        )}
                    </div>
                </Callout>
            )}
        </div>
    );
}